import type { SupabaseClient } from '@supabase/supabase-js';
import type { Moneda } from '../logica/dinero';
import type { CompraHistorica } from '../logica/clientes';
import type { ClienteImportado } from '../logica/importar';
import type { Anuncio, Campana } from '../logica/campanas';
import { cuenta, type Cliente, type Cuenta, type Despacho, type Estado, type Linea, type Pago, type Pedido, type Plantilla, type Reglas, type Transportadora } from '../logica/pedidos';
import { pyg } from '../logica/resultados';
import { mesSiguiente } from '../logica/celebracion';

// La base devuelve hasta 1000 filas por consulta: se piden por páginas para no perder nada cuando crezcan los pedidos.
export async function todas<T>(consulta: (desde: number, hasta: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>): Promise<T[]> {
  const out: T[] = [];
  for (let desde = 0; ; desde += 1000) {
    const { data, error } = await consulta(desde, desde + 999);
    if (error) throw new Error(error.message);
    out.push(...(data || []));
    if (!data || data.length < 1000) return out;
  }
}

export interface ProductoVenta { sku: string; nombre: string; categoria: string; grupo_talla: string; colores: string[] | null; descripcion: string | null; activo: boolean; costo: number }
// Todo lo que el formulario de pedidos necesita de Ajustes y del catálogo.
export interface Config extends Reglas {
  tasas: Record<Moneda, number>;
  canales: { id: number; nombre: string; activo: boolean }[];
  tallas: Record<string, string[]>;
  colores: { nombre: string; hex: string }[];
  productos: ProductoVenta[];
  precios: Map<string, number>; // "sku|moneda" → valor
  tarifa: { valor: number; activo: boolean };
  dias_entrega: number;
  transportadoras: Transportadora[];
  motivos: string[];
  campanas: Campana[];          // campañas de pauta con sus anuncios, para atribuir pedidos
  mensajes: { id: string; nombre: string; creada: string }[];  // campañas de mensajes uno a uno
  whatsapp_negocio: string;     // para los enlaces wa.me de los anuncios
}
export const precioDe = (cfg: Config, sku: string, mon: Moneda) => cfg.precios.get(sku + '|' + mon) || 0;

export async function cargarConfig(sb: SupabaseClient): Promise<Config> {
  const [par, mon, can, cue, tal, col, prod, cat, pre, cos, cmp, anu, men] = await Promise.all([
    sb.from('parametros').select('clave, valor'),
    sb.from('monedas').select('codigo, tasa_cop'),
    sb.from('canales').select('id, nombre, activo').order('id'),
    sb.from('cuentas_bancarias').select('banco, moneda, tipo, numero, titular, documento, activo').order('id'),
    sb.from('tallas').select('grupo, talla, orden').order('orden'),
    sb.from('colores_tela').select('nombre, hex, activo').order('id'),
    sb.from('productos').select('sku, nombre, categoria_id, grupo_talla, colores, descripcion, activo').order('sku'),
    sb.from('categorias').select('id, nombre').eq('ambito', 'producto'),
    sb.from('precios').select('sku, moneda, valor'),
    sb.from('v_costeo_producto').select('sku, total'),
    sb.from('campanas_pauta').select(COLS_CAMPANA).order('id'),
    sb.from('anuncios').select(COLS_ANUNCIO).order('id'),
    sb.from('campanas').select('id, nombre, creada').order('id', { ascending: false }),
  ]);
  const error = [par, mon, can, cue, tal, col, prod, cat, pre, cos, cmp, anu, men].find(r => r.error)?.error;
  if (error) throw new Error(error.message);
  const P = Object.fromEntries((par.data || []).map(r => [r.clave, r.valor]));
  const cats = new Map((cat.data || []).map(c => [c.id as number, c.nombre as string]));
  const costos = new Map((cos.data || []).map(c => [c.sku as string, Math.round(+c.total)]));
  const tallas: Record<string, string[]> = {};
  for (const t of tal.data || []) (tallas[t.grupo] ||= []).push(t.talla);
  const productos: ProductoVenta[] = (prod.data || []).map(p => ({ ...p, categoria: (p.categoria_id != null && cats.get(p.categoria_id)) || '', costo: costos.get(p.sku) || 0 }));
  return {
    vigencia_dias: +(P.vigencia_cotizacion?.dias ?? 15),
    anticipo_pct: +(P.anticipo?.pct ?? 50),
    condiciones: P.condiciones?.texto || '',
    cuentas: (cue.data || []).map(c => ({ ...c, moneda: String(c.moneda).trim() as Moneda })) as Cuenta[],
    pasarela: P.pasarela?.activa ? P.pasarela.nombre : null,
    plantillas: { pedido: (P.mensaje_pedido || {}) as Plantilla, cotizacion: (P.mensaje_cotizacion || {}) as Plantilla, envio: (P.mensaje_envio || {}) as Plantilla },
    nombreProducto: sku => productos.find(p => p.sku === sku)?.nombre || sku,
    tasas: Object.fromEntries((mon.data || []).map(m => [String(m.codigo).trim(), +m.tasa_cop])) as Record<Moneda, number>,
    canales: can.data || [],
    tallas,
    colores: (col.data || []).filter(c => c.activo),
    productos,
    precios: new Map((pre.data || []).map(p => [`${p.sku}|${String(p.moneda).trim()}`, +p.valor])),
    tarifa: { valor: +(P.envio_estandar?.valor ?? 0), activo: P.envio_estandar?.activo !== false },
    dias_entrega: +(P.dias_entrega?.dias_habiles ?? 15),
    transportadoras: (P.transportadoras || []).map((t: string | Transportadora) => (typeof t === 'string' ? { nombre: t, rastreo: '' } : t)).filter((t: Transportadora) => t.nombre !== 'Otra'),
    motivos: P.motivos_anulacion || [],
    campanas: armarCampanas(cmp.data || [], anu.data || []),
    mensajes: men.data || [],
    whatsapp_negocio: P.whatsapp_negocio?.numero || '',
  };
}

// Campañas de pauta con sus anuncios.
export const COLS_CAMPANA = 'id, codigo, nombre, objetivo, plataforma, inicio, fin, presupuesto, estado, segmento, notas, tope_cpp';
export const COLS_ANUNCIO = 'id, campana_id, codigo, nombre, pieza, mensaje, activo';
export function armarCampanas(cmp: Omit<Campana, 'anuncios'>[], anu: Anuncio[]): Campana[] {
  return cmp.map(c => ({ ...c, presupuesto: +c.presupuesto, tope_cpp: c.tope_cpp == null ? null : +c.tope_cpp, anuncios: anu.filter(a => a.campana_id === c.id) }));
}

type FilaPedido = Omit<Pedido, 'despacho' | 'lineas' | 'pagos' | 'moneda'> & { moneda: string; despacho_a_quien_compra: boolean; destinatario_nombre: string | null; destinatario_tel: string | null; destinatario_tel_pais: string | null;
  direccion_1: string | null; direccion_2: string | null; ciudad: string | null; codigo_postal: string | null; pais_destino: string | null };
type FilaLinea = Omit<Linea, 'color' | 'personalizacion'> & { pedido_id: string; color_tela: string | null; personalizacion: string | null };
type FilaPago = Pago & { pedido_id: string };

const COLS_PEDIDO = 'id, fecha, fecha_cotizacion, estado, estado_anterior, anulado_en, motivo_anulacion, anulado_producido, pauta_id, anuncio_id, mensajes_id, cliente_id, canal_id, moneda, tasa_cop, descuento_pct, ofrece_anticipo, medio_pago, envio, flete, envio_prometido, enviado_en, transportadora, guia, costo_envio, despacho_a_quien_compra, destinatario_nombre, destinatario_tel, destinatario_tel_pais, direccion_1, direccion_2, ciudad, codigo_postal, pais_destino, notas, origen, actualizado';
// Todas las columnas (no una lista) para que la app también funcione si la base aún no tiene `archivado_en`: esos clientes se leen como activos.
const COLS_CLIENTE = '*';

function armar(f: FilaPedido, lineas: FilaLinea[], pagos: FilaPago[]): Pedido {
  const despacho: Despacho = { mismo: f.despacho_a_quien_compra, nombre: f.destinatario_nombre || '', tel: f.destinatario_tel || '', tel_pais: f.destinatario_tel_pais || '',
    dir1: f.direccion_1 || '', dir2: f.direccion_2 || '', ciudad: f.ciudad || '', cp: f.codigo_postal || '', pais: f.pais_destino || '' };
  return {
    id: f.id, fecha: f.fecha, fecha_cotizacion: f.fecha_cotizacion, estado: f.estado, estado_anterior: f.estado_anterior, anulado_en: f.anulado_en, motivo_anulacion: f.motivo_anulacion, anulado_producido: f.anulado_producido,
    cliente_id: f.cliente_id, canal_id: f.canal_id, moneda: f.moneda.trim() as Moneda, tasa_cop: +f.tasa_cop, descuento_pct: +f.descuento_pct, ofrece_anticipo: f.ofrece_anticipo,
    medio_pago: f.medio_pago, envio: f.envio, flete: +f.flete, envio_prometido: f.envio_prometido, enviado_en: f.enviado_en, transportadora: f.transportadora, guia: f.guia, costo_envio: f.costo_envio == null ? null : +f.costo_envio,
    despacho, notas: f.notas, origen: f.origen, actualizado: f.actualizado, pauta_id: f.pauta_id ?? null, anuncio_id: f.anuncio_id ?? null, mensajes_id: f.mensajes_id ?? null,
    lineas: lineas.map(l => ({ id: l.id, sku: l.sku, talla: l.talla, cantidad: +l.cantidad, color: l.color_tela || '', personalizacion: l.personalizacion || '', precio_unit: +l.precio_unit, costo_unit: +l.costo_unit, grupo: l.grupo, iguales: l.iguales, imagenes: l.imagenes || [] })),
    pagos: pagos.map(p => ({ id: p.id, fecha: p.fecha, valor: +p.valor, medio: p.medio || '' })),
  };
}
const agrupado = <T extends { pedido_id: string }>(filas: T[]) => { const m = new Map<string, T[]>(); for (const f of filas) { if (!m.has(f.pedido_id)) m.set(f.pedido_id, []); m.get(f.pedido_id)!.push(f); } return m; };

export async function cargarPedidos(sb: SupabaseClient): Promise<{ pedidos: Pedido[]; clientes: Map<string, Cliente> }> {
  const [ped, lin, pag, cli] = await Promise.all([
    todas<FilaPedido>((a, b) => sb.from('pedidos').select(COLS_PEDIDO).order('id').range(a, b)),
    todas<FilaLinea>((a, b) => sb.from('pedido_lineas').select('id, pedido_id, sku, talla, cantidad, color_tela, personalizacion, precio_unit, costo_unit, grupo, iguales, imagenes').order('id').range(a, b)),
    todas<FilaPago>((a, b) => sb.from('pagos').select('id, pedido_id, fecha, valor, medio').order('id').range(a, b)),
    todas<Cliente>((a, b) => sb.from('clientes').select(COLS_CLIENTE).order('id').range(a, b)),
  ]);
  const L = agrupado(lin), G = agrupado(pag);
  return { pedidos: ped.map(f => armar(f, L.get(f.id) || [], G.get(f.id) || [])), clientes: new Map(cli.map(k => [k.id, k])) };
}

export async function cargarPedido(sb: SupabaseClient, id: string): Promise<{ pedido: Pedido; cliente: Cliente } | null> {
  const { data: f, error } = await sb.from('pedidos').select(COLS_PEDIDO).eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  if (!f) return null;
  const [lin, pag, cli] = await Promise.all([
    sb.from('pedido_lineas').select('id, pedido_id, sku, talla, cantidad, color_tela, personalizacion, precio_unit, costo_unit, grupo, iguales, imagenes').eq('pedido_id', id).order('id'),
    sb.from('pagos').select('id, pedido_id, fecha, valor, medio').eq('pedido_id', id).order('id'),
    sb.from('clientes').select(COLS_CLIENTE).eq('id', f.cliente_id).single(),
  ]);
  const e = [lin, pag, cli].find(r => r.error)?.error;
  if (e) throw new Error(e.message);
  return { pedido: armar(f as FilaPedido, (lin.data || []) as FilaLinea[], (pag.data || []) as FilaPago[]), cliente: cli.data as Cliente };
}

// Compras de antes del sistema (importadas), para el resumen de clientes.
const COLS_HIST = 'cliente_id, fecha, producto, cantidad, valor';
const historica = (h: CompraHistorica) => ({ ...h, cantidad: +h.cantidad, valor: +h.valor });
export async function cargarHistoricas(sb: SupabaseClient): Promise<CompraHistorica[]> {
  return (await todas<CompraHistorica>((a, b) => sb.from('compras_historicas').select(COLS_HIST).order('id').range(a, b))).map(historica);
}

// Importa clientes y sus compras por lotes (cada lote queda completo o no queda). Devuelve los totales.
export async function importarClientes(sb: SupabaseClient, fuente: string, clientes: ClienteImportado[], avance: (hechos: number) => void) {
  const tot = { nuevos: 0, actualizados: 0, compras: 0 };
  for (let i = 0; i < clientes.length; i += 200) {
    const lote = clientes.slice(i, i + 200).map(({ whatsapp, pais, nombre, email, notas, compras }) => ({ whatsapp, pais, nombre, email, notas, compras }));
    const { data, error } = await sb.rpc('importar_clientes', { datos: { fuente, clientes: lote } });
    if (error) throw new Error(`Se importaron ${i} de ${clientes.length} clientes y el resto no: ${error.message}`);
    for (const k of Object.keys(tot) as (keyof typeof tot)[]) tot[k] += +(data as Record<string, number>)[k] || 0;
    avance(Math.min(clientes.length, i + 200));
  }
  return tot;
}

// Pedidos (con prendas y pagos) que cumplen una condición: los de un cliente, los de una campaña (null: los de cualquier campaña).
export async function pedidosDonde(sb: SupabaseClient, columna: 'cliente_id' | 'pauta_id' | 'mensajes_id', valor: string | number | null): Promise<Pedido[]> {
  const filas = await todas<FilaPedido>((a, b) => (valor == null ? sb.from('pedidos').select(COLS_PEDIDO).not(columna, 'is', null) : sb.from('pedidos').select(COLS_PEDIDO).eq(columna, valor)).order('id').range(a, b));
  const ids = filas.map(f => f.id);
  if (!ids.length) return [];
  const [lin, pag] = await Promise.all([
    todas<FilaLinea>((a, b) => sb.from('pedido_lineas').select('id, pedido_id, sku, talla, cantidad, color_tela, personalizacion, precio_unit, costo_unit, grupo, iguales, imagenes').in('pedido_id', ids).order('id').range(a, b)),
    todas<FilaPago>((a, b) => sb.from('pagos').select('id, pedido_id, fecha, valor, medio').in('pedido_id', ids).order('id').range(a, b)),
  ]);
  const L = agrupado(lin), G = agrupado(pag);
  return filas.map(f => armar(f, L.get(f.id) || [], G.get(f.id) || []));
}

// Ficha del cliente: sus datos, todos sus pedidos y cotizaciones, y sus compras anteriores al sistema.
export async function cargarCliente(sb: SupabaseClient, id: string): Promise<{ cliente: Cliente; pedidos: Pedido[]; historicas: CompraHistorica[] } | null> {
  const [cli, his, pedidos] = await Promise.all([
    sb.from('clientes').select(COLS_CLIENTE).eq('id', id).maybeSingle(),
    sb.from('compras_historicas').select(COLS_HIST).eq('cliente_id', id).order('fecha', { ascending: false }),
    pedidosDonde(sb, 'cliente_id', id),
  ]);
  const e = [cli, his].find(r => r.error)?.error;
  if (e) throw new Error(e.message);
  if (!cli.data) return null;
  return { cliente: cli.data as Cliente, pedidos, historicas: ((his.data || []) as CompraHistorica[]).map(historica) };
}

// Cuántos pedidos que cuentan como venta tiene cada cliente (para "Cliente recurrente"), sin contar el que se edita.
export async function pedidosPorCliente(sb: SupabaseClient, excluir: string | null): Promise<Map<string, number>> {
  const filas = await todas<{ id: string; cliente_id: string; estado: Estado }>((a, b) => sb.from('pedidos').select('id, cliente_id, estado').order('id').range(a, b));
  const m = new Map<string, number>();
  for (const p of filas) if (p.id !== excluir && p.estado !== 'cotizacion' && p.estado !== 'anulado' && p.estado !== 'por_verificar') m.set(p.cliente_id, (m.get(p.cliente_id) || 0) + 1);
  return m;
}

// Ventas netas en pesos de un mes ('AAAA-MM'), con el mismo criterio de Resultados y del Tablero (pedidos que cuentan como venta,
// por la fecha del pedido). Solo trae los pedidos de ese mes: sirve para saber si un pedido nuevo hizo cruzar un hito de ventas.
export async function ventasDelMes(sb: SupabaseClient, mes: string): Promise<number> {
  const desde = mes + '-01', hasta = mesSiguiente(mes) + '-01';
  const filas = (await todas<FilaPedido>((a, b) => sb.from('pedidos').select(COLS_PEDIDO).gte('fecha', desde).lt('fecha', hasta).order('id').range(a, b))).filter(cuenta);
  if (!filas.length) return 0;
  const lin: FilaLinea[] = [];
  for (let i = 0; i < filas.length; i += 100) {
    const ids = filas.slice(i, i + 100).map(f => f.id);
    lin.push(...await todas<FilaLinea>((a, b) => sb.from('pedido_lineas').select('id, pedido_id, sku, talla, cantidad, color_tela, personalizacion, precio_unit, costo_unit, grupo, iguales, imagenes').in('pedido_id', ids).order('id').range(a, b)));
  }
  const L = agrupado(lin);
  return pyg(filas.map(f => armar(f, L.get(f.id) || [], [])), [], new Set(), +mes.slice(0, 4)).meses[+mes.slice(5, 7) - 1].netas;
}

export async function buscarClientes(sb: SupabaseClient): Promise<Cliente[]> {
  return todas<Cliente>((a, b) => sb.from('clientes').select(COLS_CLIENTE).order('id').range(a, b));
}

// Guarda todo el pedido en una sola operación de la base (supabase/migrations/20261002150000_pedidos.sql).
export async function guardarPedido(sb: SupabaseClient, datos: Record<string, unknown>): Promise<string> {
  const { data, error } = await sb.rpc('guardar_pedido', { datos });
  if (error) throw new Error(error.message);
  return data as string;
}

// Cambios de estado (enviado, anulado, reactivado). Si otra persona cambió el pedido mientras tanto, no se pisa su cambio.
export async function cambiarEstado(sb: SupabaseClient, p: Pick<Pedido, 'id' | 'actualizado'>, cambios: Partial<Record<string, string | number | boolean | null>> & { estado: Estado }) {
  const { data, error } = await sb.from('pedidos').update(cambios).eq('id', p.id).eq('actualizado', p.actualizado).select('id');
  if (error) throw new Error(error.message);
  if (!data || !data.length) throw new Error(`El pedido ${p.id} cambió mientras lo veías (quizás desde otro equipo). Recarga la página y vuelve a intentarlo.`);
}

// Observaciones del pedido (columna `notas`): cualquier requerimiento específico de ese pedido. Se guardan desde la lista de pedidos.
// Si otra persona cambió el pedido mientras tanto, no se pisa su cambio. Devuelve la nueva marca de actualización del pedido.
export async function guardarObservacion(sb: SupabaseClient, p: Pick<Pedido, 'id' | 'actualizado'>, texto: string): Promise<string> {
  const { data, error } = await sb.from('pedidos').update({ notas: texto.trim() || null }).eq('id', p.id).eq('actualizado', p.actualizado).select('id, actualizado');
  if (error) throw new Error(error.message);
  if (!data || !data.length) throw new Error(`El pedido ${p.id} cambió mientras lo veías (quizás desde otro equipo). Recarga la página y vuelve a escribir la observación.`);
  return data[0].actualizado as string;
}

export async function guardarPrecio(sb: SupabaseClient, sku: string, moneda: Moneda, valor: number) {
  const { error } = await sb.from('precios').upsert({ sku, moneda, valor });
  if (error) throw new Error(error.message);
}

// Datos que se editan en la ficha del cliente. El WhatsApp y el país no se cambian aquí: son la llave del cliente.
export async function actualizarCliente(sb: SupabaseClient, id: string, cambios: Partial<Pick<Cliente, 'nombre' | 'email' | 'acepta_mensajes' | 'acepta_fecha' | 'notas'>>) {
  const { error } = await sb.from('clientes').update(cambios).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function guardarCorreo(sb: SupabaseClient, clienteId: string, email: string) {
  const { error } = await sb.from('clientes').update({ email }).eq('id', clienteId);
  if (error) throw new Error(error.message);
}

// "Borrar" un cliente: se borra de verdad si no tiene pedidos, cotizaciones, enlaces ni campañas; si los tiene, se archiva. Nunca toca un pedido.
export async function eliminarCliente(sb: SupabaseClient, id: string): Promise<'borrado' | 'archivado'> {
  const { data, error } = await sb.rpc('eliminar_cliente', { p_id: id });
  if (error) throw new Error(error.message);
  return data as 'borrado' | 'archivado';
}
